import { useEffect, useMemo } from 'preact/hooks';

import { nanoid } from 'nanoid';
import useWebSocket from 'react-use-websocket';

import { parseJSON } from '@/shared/helpers/parse-json';
import {
  getPdTma,
  useAppActions,
  useAppState,
} from '@/shared/stores/app-store/app-store';

import { INIT_WS_OPTIONS } from './constants';
import { logMessage, makeMessage } from './helpers';
import { type CbType, type Message, MessageAction, MessageType } from './types';

let wsSentInitialMsg = false;

export const useWs = () => {
  const callbackStore = useMemo(() => new Map<MessageType, Array<CbType>>(), []);

  const { token, sessionId, startAppArgs, isLiteMode, isExclusiveMode } = useAppState();
  const { hydrate } = useAppActions();

  function getWebSocketUrl(isExclusive: boolean, isLite: boolean): string {
    if (isExclusive) return import.meta.env.VITE_APP_WS_URL_EXCLUSIVE;
    if (isLite) return import.meta.env.VITE_APP_WS_URL_LITE;
    return import.meta.env.VITE_APP_WS_URL;
  }

  const wsUrl = getWebSocketUrl(isExclusiveMode, isLiteMode);

  const {
    sendMessage: sendMessageLib,
    lastJsonMessage,
    lastMessage,
    getWebSocket,
    readyState,
  } = useWebSocket<Message>(wsUrl, {
    ...INIT_WS_OPTIONS,
    onOpen: () => {
      if (!wsSentInitialMsg) {
        wsSentInitialMsg = true;
        registerSocketConnection();
        hydrate({ wsSentInitialMsg });
      }
    },
    onClose: () => {
      wsSentInitialMsg = false;
      hydrate({ wsSentInitialMsg });
    },
  });

  const sendMessage = (type: MessageType, action: MessageAction, payload: any = {}) => {
    logMessage(type, { action, payload });

    sendMessageLib(makeMessage(type, action, payload));
  };

  const subscribe = (type: MessageType, cb?: CbType) => {
    const uuid = nanoid();

    sendMessage(type, MessageAction.SUBSCRIBE, { subscription_id: uuid });
    if (cb) {
      manageCbs?.(type, cb);
    }

    return uuid;
  };

  const unsubscribe = (type: MessageType, id: string) => {
    sendMessage(type, MessageAction.UNSUBSCRIBE, { subscription_id: id });
  };

  const manageCbs = (type: MessageType, cb: CbType) => {
    const typeCbs = callbackStore.has(type);
    if (typeCbs) {
      const arr = callbackStore.get(type);
      if (arr) callbackStore.set(type, [...arr, cb]);
    } else {
      callbackStore.set(type, [cb]);
    }
  };

  const registerSocketConnection = () => {
    const pdTma = getPdTma();
    const payload: {
      token: string;
      game_session_id: string;
      src: undefined | string;
      TMAsrc?: string;
    } = {
      token,
      game_session_id: sessionId,
      src: startAppArgs?.src,
      ...(pdTma && { TMAsrc: pdTma }),
    };

    sendMessage(MessageType.SIGNIN, MessageAction.EXECUTE, payload);
  };

  useEffect(() => {
    if (!lastMessage) return;

    const parsedData = parseJSON<Message>(lastMessage?.data);

    const type = parsedData?.type;
    const action = parsedData?.action;

    if (type && callbackStore.has(type) && action === MessageAction.NOTIFICATION) {
      callbackStore.get(type)?.forEach((cb) => cb(parsedData));
    }
  }, [callbackStore, lastMessage]);

  return {
    getWebSocket,

    lastMessage,
    lastJsonMessage,

    sendMessage,

    subscribe,
    unsubscribe,

    readyState,
  };
};

export type UseWsReturnType = ReturnType<typeof useWs>;
